import React, {useId} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Link as RouteLink} from 'react-router-dom'
import {
    Box,
    AspectRatio,
    LinkBox,
    Skeleton,
    Text,
    useBreakpointValue,
    Flex,
    LinkOverlay
} from '@chakra-ui/react'
import DynamicImage from '@app/components/dynamic-image'
import PDCTitle from '../../../components/pdc-title'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {pageUrlBuilder} from '@app/utils/url-utils'
import {DYNAMIC_IMAGE_WIDTHS} from '@app/constants.js'
import useMultiSite from '../../../hooks/use-multi-site.js'

/**
 * PDC About Story Block Excerpt
 * @param {object} page - Page data
 * @param {string} backgroundColor - Background color
 * @param {string} textColor - Text color
 * @param {string} alternativePos - Secondary images and text position
 * @returns {JSX.Element} - PDC About Story Block Excerpt component
 */
export const SimpleMagazineBlogPost = ({page, ...props}) => {
    const intl = useIntl()
    const {buildUrl} = useMultiSite()
    const aboutStoryBlockDefine = page.aboutStoryBlockDefine
    const isMobile = useBreakpointValue({base: true, md: false})

    const titleId = useId()
    const currentRatio = useBreakpointValue({base: 3 / 4, md: 4 / 5})

    // Get current main image based on breakpoint
    const currentMainImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: aboutStoryBlockDefine?.smallImage,
            mediumEl: aboutStoryBlockDefine?.mediumImage,
            largeEl: aboutStoryBlockDefine?.largeImage,
            xlargeEl: aboutStoryBlockDefine?.xlargeImage,
            xxlargeEl: aboutStoryBlockDefine?.xxlargeImage
        })
    )

    return (
        <Box {...props}>
            <Skeleton
                isLoaded={!!currentMainImage}
                minWidth={'100%'}
                minHeight={['200px', null, '400px']}
                startColor="lightBlue"
                endColor="white"
            >
                <LinkBox
                    as="article"
                    aria-describedby={titleId}
                    flexDirection={['row', null, 'column']}
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    width="100%"
                >
                    <Box flex={['1 0 45%', null, '1 0 100%']} w="full" position="relative">
                        <AspectRatio ratio={currentRatio}>
                            <DynamicImage
                                src={`${currentMainImage?.url}[?sw={width}]`}
                                widths={DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice}
                                height="100%"
                                imageProps={{
                                    width: '100%',
                                    loading: aboutStoryBlockDefine?.displayPriority
                                        ? 'eager'
                                        : 'lazy',
                                    fetchpriority: aboutStoryBlockDefine?.displayPriority
                                        ? 'high'
                                        : null,
                                    alt:
                                        aboutStoryBlockDefine?.imageAlt === 'empty'
                                            ? ''
                                            : aboutStoryBlockDefine?.imageAlt ||
                                              aboutStoryBlockDefine?.title,
                                    title:
                                        aboutStoryBlockDefine?.imageTitle === 'empty'
                                            ? ''
                                            : aboutStoryBlockDefine?.imageTitle ||
                                              aboutStoryBlockDefine?.title
                                }}
                            />
                        </AspectRatio>
                    </Box>
                    <Box mt={['20px', null, '34px']} ml={['14px', null, 0]} w="full">
                        <Flex flexDirection={['column', null, 'row']}>
                            <Text
                                dangerouslySetInnerHTML={{
                                    __html: `${new Date(page?.publicationDate)?.toLocaleDateString(
                                        intl.locale,
                                        {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        }
                                    )}&nbsp;-&nbsp;`
                                }}
                                variant="eyeBrowSmall"
                                color={aboutStoryBlockDefine?.textColor || 'fullBlack'}
                                textTransform={'uppercase'}
                                align={'left'}
                            />
                            <Text
                                variant="eyeBrowSmall"
                                color={aboutStoryBlockDefine?.textColor || 'fullBlack'}
                                textTransform={'uppercase'}
                                align={'left'}
                            >
                                {page.folderName}
                            </Text>
                        </Flex>
                        <PDCTitle
                            title={aboutStoryBlockDefine?.title || ''}
                            titleFontColor={aboutStoryBlockDefine?.textColor || 'fullblack'}
                            titleHeadingLevel={'h2'}
                            fontSize="2rem"
                            id={titleId}
                            mt={'8px'}
                            txtHorizontalPos={'left'}
                        />
                        {!isMobile && (
                            <Text
                                dangerouslySetInnerHTML={{
                                    __html: aboutStoryBlockDefine?.desc
                                }}
                                variant="bodyLarge2"
                                color={aboutStoryBlockDefine?.textColor || 'darkGray'}
                                mt={'24px'}
                                align={'left'}
                            />
                        )}
                        <Box mt={['8px', null, '20px']} textAlign={'left'}>
                            <LinkOverlay as={RouteLink} to={buildUrl(pageUrlBuilder(page?.id))}>
                                <Text
                                    variant="link"
                                    as="span"
                                    textColor={aboutStoryBlockDefine?.textColor || 'fullBlack'}
                                >
                                    {intl.formatMessage({
                                        id: 'magazine.link.read_more',
                                        defaultMessage: 'Read more'
                                    })}
                                </Text>
                            </LinkOverlay>
                        </Box>
                    </Box>
                </LinkBox>
            </Skeleton>
        </Box>
    )
}

SimpleMagazineBlogPost.propTypes = {
    page: PropTypes.object,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    alternativePos: PropTypes.bool
}
